import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import Spinner from "./Spinner";

interface BranchRow {
  id: string;
  branch: string;
  country: string;
  officer_name: string;
  role: string;
}

type Draft = Omit<BranchRow, "id">;

const BLANK: Draft = { branch: "", country: "", officer_name: "", role: "Branch Officer" };
const ROLES = ["Branch Officer", "Head Branch Officer"];

function errorText(e: any): string {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d: any) => `${(d.loc ?? []).filter((l: any) => l !== "body").join(".") || "form"}: ${String(d.msg).replace(/^Value error, /, "")}`)
      .join("\n");
  }
  if (typeof detail === "string") return detail;
  return "Request failed. Please try again.";
}

const unique = (values: string[]) => Array.from(new Set(values.filter(Boolean))).sort();

export default function BranchesManager() {
  const [rows, setRows] = useState<BranchRow[] | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get<BranchRow[]>("/branches");
      setRows(data);
    } catch (e) {
      setRows([]);
      setError(errorText(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openForm = (row?: BranchRow) => {
    setError(null);
    setNotice(null);
    setEditId(row?.id ?? null);
    setDraft(row ? { branch: row.branch, country: row.country, officer_name: row.officer_name, role: row.role || "Branch Officer" } : BLANK);
    if (row) window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeForm = () => { setDraft(null); setEditId(null); setError(null); };

  // Picking a known branch fills in its country.
  const setBranch = (branch: string) => {
    if (!draft) return;
    const known = rows?.find((r) => r.branch.toLowerCase() === branch.trim().toLowerCase());
    setDraft({ ...draft, branch, country: known && !draft.country ? known.country : draft.country });
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      if (editId) {
        const { data } = await api.put<BranchRow>(`/branches/${editId}`, draft);
        setNotice(`Updated ${data.officer_name} (${data.branch}).`);
      } else {
        const { data } = await api.post<BranchRow>("/branches", draft);
        setNotice(`Added ${data.officer_name} to ${data.branch}.`);
      }
      closeForm();
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: BranchRow) => {
    if (!window.confirm(`Remove ${row.officer_name} from ${row.branch}?\n\nThis can't be undone.`)) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.delete(`/branches/${row.id}`);
      setNotice(`Removed ${row.officer_name} from ${row.branch}.`);
      if (editId === row.id) closeForm();
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const q = filter.trim().toLowerCase();
  const visible = (rows ?? [])
    .filter((r) => !q || `${r.branch} ${r.country} ${r.officer_name} ${r.role}`.toLowerCase().includes(q))
    .sort((a, b) => a.branch.localeCompare(b.branch) || (a.role === b.role ? 0 : a.role.startsWith("Head") ? -1 : 1));
  const branchCount = unique((rows ?? []).map((r) => r.branch)).length;

  return (
    <div style={S.card}>
      <div style={S.header}>
        <h3 style={S.title}>
          Branches {rows && <span style={S.count}>({branchCount} branches, {rows.length} officers)</span>}
        </h3>
        <div style={S.headerActions}>
          <input style={S.search} placeholder="Filter by branch, country or officer"
            value={filter} onChange={(e) => setFilter(e.target.value)} />
          {!draft && <button style={S.primaryBtn} onClick={() => openForm()}>Add officer</button>}
        </div>
      </div>

      <datalist id="branch-names">{unique((rows ?? []).map((r) => r.branch)).map((b) => <option key={b} value={b} />)}</datalist>
      <datalist id="branch-countries">{unique((rows ?? []).map((r) => r.country)).map((c) => <option key={c} value={c} />)}</datalist>
      <datalist id="branch-roles">{unique([...ROLES, ...(rows ?? []).map((r) => r.role)]).map((r) => <option key={r} value={r} />)}</datalist>

      {draft && (
        <div style={S.form}>
          <label style={S.field}>
            <span style={S.label}>Branch (city)</span>
            <input style={S.input} list="branch-names" placeholder="e.g. Dallas, TX" value={draft.branch}
              onChange={(e) => setBranch(e.target.value)} />
          </label>
          <label style={S.field}>
            <span style={S.label}>Country</span>
            <input style={S.input} list="branch-countries" value={draft.country}
              onChange={(e) => setDraft({ ...draft, country: e.target.value })} />
          </label>
          <label style={S.field}>
            <span style={S.label}>Officer name</span>
            <input style={S.input} value={draft.officer_name}
              onChange={(e) => setDraft({ ...draft, officer_name: e.target.value })} />
          </label>
          <label style={S.field}>
            <span style={S.label}>Role</span>
            <input style={S.input} list="branch-roles" value={draft.role}
              onChange={(e) => setDraft({ ...draft, role: e.target.value })} />
          </label>
          <div style={S.formActions}>
            <button style={{ ...S.primaryBtn, ...(busy ? S.disabled : {}) }} disabled={busy} onClick={save}>
              {busy ? "Saving…" : editId ? "Save changes" : "Add officer"}
            </button>
            <button style={S.secondaryBtn} disabled={busy} onClick={closeForm}>Cancel</button>
          </div>
          <p style={S.hint}>
            Each row is one officer. Officers with the same branch name are shown together on the Leadership page.
          </p>
        </div>
      )}

      {notice && <div style={S.success}>{notice}</div>}
      {error && <pre style={S.error}>{error}</pre>}

      {rows === null ? <Spinner /> : (
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table style={S.table}>
            <thead>
              <tr>{["Branch", "Country", "Officer", "Role", ""].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {visible.map((r, i) => (
                <tr key={r.id} style={{
                  ...(i % 2 ? { background: "rgba(255,255,255,0.02)" } : {}),
                  ...(editId === r.id ? { background: "rgba(29,158,117,0.10)" } : {}),
                }}>
                  <td style={{ ...S.td, fontWeight: 500 }}>{r.branch}</td>
                  <td style={{ ...S.td, color: "var(--text-secondary)" }}>{r.country}</td>
                  <td style={S.td}>{r.officer_name}</td>
                  <td style={S.td}>{r.role || <span style={{ color: "var(--text-muted)" }}>-</span>}</td>
                  <td style={{ ...S.td, textAlign: "right" }}>
                    <button style={S.rowBtn} disabled={busy} onClick={() => openForm(r)}>Edit</button>
                    <button style={{ ...S.rowBtn, ...S.deleteBtn }} disabled={busy} onClick={() => remove(r)}>Delete</button>
                  </td>
                </tr>
              ))}
              {rows.length > 0 && visible.length === 0 && (
                <tr><td style={{ ...S.td, color: "var(--text-muted)" }} colSpan={5}>No officers match.</td></tr>
              )}
              {rows.length === 0 && (
                <tr><td style={{ ...S.td, color: "var(--text-muted)" }} colSpan={5}>No branches yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  card: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12, padding: "24px 20px",
    boxSizing: "border-box",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    gap: 12, flexWrap: "wrap", marginBottom: 16,
  },
  headerActions: { display: "flex", gap: 12, flexWrap: "wrap" },
  search: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8, padding: "8px 12px",
    color: "var(--text-primary)", fontSize: 13, minWidth: 220,
  },
  title: { fontSize: 14, fontWeight: 600, color: "var(--text-primary)" },
  count: { color: "var(--text-muted)", fontWeight: 400 },
  form: {
    display: "grid", gap: 16, marginBottom: 20, padding: 16, borderRadius: 8,
    background: "rgba(255,255,255,0.03)",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
  },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: {
    fontSize: 11, fontWeight: 500, letterSpacing: "0.08em",
    textTransform: "uppercase", color: "var(--text-muted)",
  },
  input: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8, padding: "10px 12px",
    color: "var(--text-primary)", fontSize: 14, width: "100%",
  },
  readOnly: { opacity: 0.6, cursor: "not-allowed" },
  formActions: { display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap" },
  hint: { gridColumn: "1 / -1", fontSize: 12, color: "var(--text-muted)" },
  primaryBtn: {
    padding: "9px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
    background: "var(--teal)", color: "#fff",
  },
  secondaryBtn: {
    padding: "9px 16px", borderRadius: 8, fontSize: 13, fontWeight: 500,
    border: "1px solid rgba(255,255,255,0.2)", color: "var(--text-primary)",
  },
  disabled: { opacity: 0.4, cursor: "not-allowed" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 560 },
  th: {
    textAlign: "left", padding: "10px 12px",
    color: "var(--text-muted)", fontSize: 11, fontWeight: 500,
    letterSpacing: "0.08em", textTransform: "uppercase",
    borderBottom: "1px solid rgba(255,255,255,0.07)", whiteSpace: "nowrap",
  },
  td: {
    padding: "10px 12px", color: "var(--text-primary)",
    borderBottom: "1px solid rgba(255,255,255,0.04)", whiteSpace: "nowrap",
  },
  rowBtn: {
    padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 500,
    border: "1px solid rgba(255,255,255,0.15)", color: "var(--text-primary)", marginLeft: 6,
  },
  deleteBtn: { borderColor: "rgba(224,90,90,0.4)", color: "#f08a8a" },
  success: {
    marginBottom: 16, padding: "10px 12px", borderRadius: 8,
    background: "rgba(29,158,117,0.12)", border: "1px solid var(--teal-dim)",
    color: "var(--teal-light)", fontSize: 13,
  },
  error: {
    marginBottom: 16, padding: "10px 12px", borderRadius: 8,
    background: "rgba(224,90,90,0.12)", border: "1px solid rgba(224,90,90,0.4)",
    color: "#f08a8a", fontSize: 13, fontFamily: "var(--font-body)", whiteSpace: "pre-wrap",
  },
};
